'use client'

import classes from './page-unavailable.module.css'

import { Button, Group, Paper, Stack, Text, ThemeIcon } from '@mantine/core'
import { CircleOff, HelpCircle, Home, LayoutGrid, Link2Off } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Link from 'next/link'

import { useAmplitude } from '@/components/providers/amplitude'
import { routes } from '@/constants/routes'
import { ZENDESK_SUPPORT_ROUTE } from '@/constants/zendesk'

type PageUnavailableProps = {
  /** `no-applications`: no enabled product. `not-found`: the URL leads nowhere. */
  reason: 'no-applications' | 'not-found'
}

const REASON_ICONS = {
  'no-applications': LayoutGrid,
  'not-found': CircleOff,
}

/**
 * Card for pages the user can't reach, with a note about links broken by the platform
 * migration. Shown by `/no-applications` and the root 404 page.
 */
const PageUnavailable = (props: PageUnavailableProps) => {
  const { reason } = props
  const t = useTranslations('page-unavailable')
  const { trackSupportClicked } = useAmplitude()
  const Icon = REASON_ICONS[reason]

  return (
    <Paper className={classes.card} p='3xl' radius='xl' shadow='sm' withBorder>
      <Stack align='center' gap='lg'>
        <ThemeIcon className={classes.icon} radius='lg' size={48} variant='default'>
          <Icon nonScalingStroke size={20} strokeWidth={1} />
        </ThemeIcon>
        <Stack align='center' gap='xs'>
          <Text className={classes.title} component='h1'>
            {t(`${reason}.title`)}
          </Text>
          <Text className={classes.description}>{t(`${reason}.description`)}</Text>
        </Stack>
        <Group align='flex-start' className={classes.notice} gap='md' wrap='nowrap'>
          <Link2Off
            className={classes.noticeIcon}
            nonScalingStroke
            size={16}
            strokeWidth={1}
          />
          <Stack gap='xs'>
            <Text className={classes.noticeTitle}>{t('migration.title')}</Text>
            <Text className={classes.description}>{t('migration.description')}</Text>
          </Stack>
        </Group>
        <Group gap='sm' justify='center'>
          <Button
            component={Link}
            href={routes.index}
            leftSection={<Home nonScalingStroke size={14} strokeWidth={1} />}
            size='xs'
            variant='default'
          >
            {t('home')}
          </Button>
          <Button
            component='a'
            href={ZENDESK_SUPPORT_ROUTE}
            leftSection={<HelpCircle nonScalingStroke size={14} strokeWidth={1} />}
            onClick={() => trackSupportClicked()}
            rel='noopener noreferrer'
            size='xs'
            target='_blank'
            variant='default'
          >
            {t('support')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

export default PageUnavailable
